import { z } from "zod";
import { nullableUuid, optionalUuid } from "../lib/zod-helpers";
import { paginationSchema, sortSchema } from "./pagination";

/**
 * HCA Moravia — validační schémata pro programové balíčky (plán §S.2.6).
 *
 * V S8 dodáváme jen schemas + tests; tRPC routery a UI modul přicházejí
 * v dalším sprintu. Schemas jsou navržené tak, aby pokryly celý modul
 * (templates + instances + seminars) a nemusely se v dalším kroku měnit.
 */

export const programStavEnum = z.enum(["AKTIVNI", "VYCERPANO", "EXPIROVANO", "ZRUSENO"]);
export type ProgramStav = z.infer<typeof programStavEnum>;

/**
 * Typ programu — hlídá ho zod, ne pg enum (sloupec je `text`).
 * BUDGET = rozpočtový rámec: čerpání překlápí stav na VYCERPANO.
 * PREDPLACENY = předplacený VIP balíček: klient si zaplatil balík dopředu
 * a jednotlivě ho odkusuje — vyčerpaný rámec je normální stav, ne varování,
 * stav se na VYCERPANO nikdy automaticky nepřeklápí.
 */
export const programTypEnum = z.enum(["BUDGET", "PREDPLACENY"]);
export type ProgramTyp = z.infer<typeof programTypEnum>;

const uuidMsg = "Neplatné UUID";

/**
 * Jedna položka složení programu — produkt, orientační cena (výchozí
 * z katalogu) a počet opakování ("VIP = 15× interní workshop").
 * Stejný tvar používá složení instance (`createProgramInstanceSchema.polozky`)
 * i složení šablony (`createProgramTemplateSchema.polozky`).
 */
export const programPolozkaSchema = z.object({
  produktId: z.string().uuid(uuidMsg),
  cena: z.coerce
    .number()
    .nonnegative("Cena nesmí být záporná")
    .max(10_000_000, "Cena je mimo rozsah"),
  pocet: z.coerce
    .number()
    .int("Počet musí být celé číslo")
    .min(1, "Počet musí být alespoň 1")
    .max(50, "Najednou lze přidat nejvíc 50 opakování")
    .default(1),
  poznamka: z.string().max(1000, "Poznámka musí být 1000 znaků nebo méně").optional(),
});

/**
 * Vytvoření šablony balíčku (např. "VIP", "Premium") včetně složení.
 * `polozky` = z jakých produktů (cena × počet) se program ze šablony založí;
 * legacy `defaultSeminarIds` (pole ID bez cen/počtů) zůstává jen pro stará data.
 */
export const createProgramTemplateSchema = z.object({
  nazev: z.string().min(1, "Název je povinný").max(255, "Název musí být 255 znaků nebo méně"),
  popis: z.string().max(5000, "Popis musí být 5000 znaků nebo méně").optional(),
  cenaCelkem: z.coerce
    .number()
    .nonnegative("Cena nesmí být záporná")
    .max(10_000_000, "Cena je mimo rozsah"),
  platnostMesicu: z.coerce
    .number()
    .int("Platnost musí být celé číslo")
    .min(1, "Platnost musí být alespoň 1 měsíc")
    .max(60, "Platnost může být nejvíc 60 měsíců")
    .default(12),
  defaultSeminarIds: z.array(z.string().uuid(uuidMsg)).max(100, "Max 100 seminářů").optional(),
  polozky: z.array(programPolozkaSchema).max(100, "Max 100 položek šablony").optional(),
  aktivni: z.coerce.boolean().default(true),
});

/**
 * Úprava šablony — `polozky: undefined` = složení beze změny,
 * vyplněné pole = replace-all (stejný vzor jako šablony checklistů).
 */
export const updateProgramTemplateSchema = createProgramTemplateSchema.partial().extend({
  id: z.string().uuid(uuidMsg),
});

export const listProgramTemplatesSchema = paginationSchema.extend({
  aktivni: z.coerce.boolean().optional(),
  search: z.string().max(255, "Hledaný text musí být 255 znaků nebo méně").optional(),
});

/**
 * Trvalé smazání šablony — server pustí jen šablonu, na kterou se
 * neodkazuje žádný program (jinak deaktivace přes `aktivni: false`).
 */
export const deleteProgramTemplateSchema = z.object({
  id: z.string().uuid(uuidMsg),
});

/**
 * Založení instance programu u konkrétní firmy nebo klienta.
 * Alespoň jedno z `firmaId` / `klientId` musí být vyplněné.
 * `polozky` = složení programu naklikané rovnou při zakládání.
 */
export const createProgramInstanceSchema = z
  .object({
    templateId: optionalUuid(),
    firmaId: optionalUuid(),
    klientId: optionalUuid(),
    typ: programTypEnum.default("BUDGET"),
    nazev: z.string().min(1, "Název je povinný").max(255, "Název musí být 255 znaků nebo méně"),
    cenaRamce: z.coerce
      .number()
      .nonnegative("Cena nesmí být záporná")
      .max(10_000_000, "Cena je mimo rozsah"),
    platnostOd: z.coerce.date(),
    platnostDo: z.coerce.date(),
    poznamka: z.string().max(5000, "Poznámka musí být 5000 znaků nebo méně").optional(),
    polozky: z.array(programPolozkaSchema).max(100, "Max 100 položek programu").optional(),
  })
  .refine((data) => data.firmaId || data.klientId, {
    message: "Program musí být přiřazen firmě nebo klientovi.",
    path: ["firmaId"],
  })
  .refine((data) => data.platnostDo > data.platnostOd, {
    message: "Platnost do musí být po platnost od.",
    path: ["platnostDo"],
  });

export const updateProgramInstanceSchema = z.object({
  id: z.string().uuid(uuidMsg),
  nazev: z
    .string()
    .min(1, "Název je povinný")
    .max(255, "Název musí být 255 znaků nebo méně")
    .optional(),
  /** `undefined` = beze změny, `null`/"" = odpojit klienta od programu. */
  klientId: nullableUuid(uuidMsg),
  typ: programTypEnum.optional(),
  cenaRamce: z.coerce
    .number()
    .nonnegative("Cena nesmí být záporná")
    .max(10_000_000, "Cena je mimo rozsah")
    .optional(),
  stav: programStavEnum.optional(),
  poznamka: z.string().max(5000, "Poznámka musí být 5000 znaků nebo méně").nullable().optional(),
});

/** Whitelist řazení programů — router mapuje klíče na sloupce. */
export const PROGRAM_INSTANCES_SORT_KEYS = [
  "nazev",
  "zakaznik",
  "platnost",
  "cerpano",
  "vytvoreno",
] as const;
export type ProgramInstancesSortKey = (typeof PROGRAM_INSTANCES_SORT_KEYS)[number];

export const listProgramInstancesSchema = paginationSchema
  .merge(sortSchema(PROGRAM_INSTANCES_SORT_KEYS))
  .extend({
    stav: programStavEnum.optional(),
    /** Filtr na víc stavů najednou (picker termínů chce AKTIVNI + VYCERPANO). */
    stavy: z.array(programStavEnum).max(4, "Maximálně 4 stavy").optional(),
    firmaId: optionalUuid(uuidMsg),
    klientId: optionalUuid(uuidMsg),
    /** Fulltext přes název programu / firmu / klienta / poznámku — trim, prázdné = bez filtru. */
    search: z
      .string()
      .trim()
      .max(255, "Hledaný text musí být 255 znaků nebo méně")
      .optional()
      .transform((v) => (v ? v : undefined)),
  });

/**
 * Přidání položky do programu (čerpání z rámce). Položka smí vzniknout
 * jako pouhý plán (bez `planovanyTermin`) a termín se naváže později.
 * `pocet` vloží rovnou N stejných řádků — „15× interní workshop" bez
 * patnáctinásobného vyklikávání.
 */
export const addProgramSeminarSchema = z
  .object({
    programInstanceId: z.string().uuid(uuidMsg),
    produktId: z.string().uuid(uuidMsg),
    planovanyTermin: optionalUuid(uuidMsg),
    cenaOdectena: z.coerce
      .number()
      .nonnegative("Cena nesmí být záporná")
      .max(10_000_000, "Cena je mimo rozsah"),
    poznamka: z.string().max(1000, "Poznámka musí být 1000 znaků nebo méně").optional(),
    pocet: z.coerce
      .number()
      .int("Počet musí být celé číslo")
      .min(1, "Počet musí být alespoň 1")
      .max(50, "Najednou lze přidat nejvíc 50 opakování")
      .default(1),
  })
  .superRefine((data, ctx) => {
    // Termín jde navázat jen k jedné položce — při `pocet > 1` by se tentýž
    // termín vložil do N řádků a z rámce odečetl N×.
    if (data.planovanyTermin && data.pocet > 1) {
      ctx.addIssue({
        code: "custom",
        message:
          "Termín lze navázat jen při počtu 1 — položky přidejte bez termínu a navažte je pak jednotlivě.",
        path: ["planovanyTermin"],
      });
    }
  });

/** Úprava položky programu — cena se promítne do čerpání rámce. */
export const updateProgramSeminarSchema = z.object({
  id: z.string().uuid(uuidMsg),
  produktId: optionalUuid(uuidMsg),
  cenaOdectena: z.coerce
    .number()
    .nonnegative("Cena nesmí být záporná")
    .max(10_000_000, "Cena je mimo rozsah")
    .optional(),
  poznamka: z.string().max(1000, "Poznámka musí být 1000 znaků nebo méně").nullable().optional(),
});

/** Navázání položky na konkrétní termín (`terminId: null` = odvázání). */
export const bindProgramSeminarTerminSchema = z.object({
  id: z.string().uuid(uuidMsg),
  terminId: z.string().uuid(uuidMsg).nullable(),
  /**
   * CAS guard proti souběžnému rebindu: termín, na který je položka podle
   * klienta navázaná TEĎ (`null` = bez termínu). Když se skutečná vazba
   * mezitím změnila, server odpoví CONFLICTem místo tichého přepsání.
   * Volitelné kvůli zpětné kompatibilitě starších klientů.
   */
  expectedTerminId: z.string().uuid(uuidMsg).nullable().optional(),
});

/** Duplikace řádku složení programu — kopie produktu, ceny a poznámky. */
export const duplicateProgramSeminarSchema = z.object({
  id: z.string().uuid(uuidMsg),
  pocet: z.coerce
    .number()
    .int("Počet musí být celé číslo")
    .min(1, "Počet musí být alespoň 1")
    .max(50, "Najednou lze přidat nejvíc 50 kopií")
    .default(1),
});

/** Odebrání položky z programu — vrátí její cenu zpět do rámce. */
export const removeProgramSeminarSchema = z.object({
  id: z.string().uuid(uuidMsg),
});

/** Označení absolvování položky — `false` označení vrátí (undo bez potvrzení). */
export const markProgramSeminarAbsolvedSchema = z.object({
  id: z.string().uuid(uuidMsg),
  absolvovano: z.boolean(),
});

export const programIdSchema = z.object({
  id: z.string().uuid(uuidMsg),
});

/** Trvalé smazání programu — server ho pustí jen u programu bez čerpání. */
export const deleteProgramInstanceSchema = z.object({
  id: z.string().uuid(uuidMsg),
});

export type CreateProgramTemplateInput = z.infer<typeof createProgramTemplateSchema>;
export type UpdateProgramTemplateInput = z.infer<typeof updateProgramTemplateSchema>;
export type ListProgramTemplatesInput = z.infer<typeof listProgramTemplatesSchema>;
export type DeleteProgramTemplateInput = z.infer<typeof deleteProgramTemplateSchema>;
export type CreateProgramInstanceInput = z.infer<typeof createProgramInstanceSchema>;
export type UpdateProgramInstanceInput = z.infer<typeof updateProgramInstanceSchema>;
export type ListProgramInstancesInput = z.infer<typeof listProgramInstancesSchema>;
export type AddProgramSeminarInput = z.infer<typeof addProgramSeminarSchema>;
export type UpdateProgramSeminarInput = z.infer<typeof updateProgramSeminarSchema>;
export type BindProgramSeminarTerminInput = z.infer<typeof bindProgramSeminarTerminSchema>;
export type DuplicateProgramSeminarInput = z.infer<typeof duplicateProgramSeminarSchema>;
export type RemoveProgramSeminarInput = z.infer<typeof removeProgramSeminarSchema>;
export type MarkProgramSeminarAbsolvedInput = z.infer<typeof markProgramSeminarAbsolvedSchema>;
export type ProgramIdInput = z.infer<typeof programIdSchema>;
export type DeleteProgramInstanceInput = z.infer<typeof deleteProgramInstanceSchema>;
export type ProgramPolozkaInput = z.infer<typeof programPolozkaSchema>;
